import React, { useState, useEffect } from "react";
import useAuth from "../../Auth/useAuth";
import "./Profile.css";
import AlertModal from "../AlertModal/AlertModal";
import config from "../../config";
import axios from "axios";
import DriverNavbar from "../DriverNavbar/DriverNavbar";

const getProfileUrl = `${config.BASE_URL}/get-profile`;
const updateProfileUrl = `${config.BASE_URL}/update-profile`;

const Profile = () => {
  const { username, userProfile, setActiveModule } = useAuth();
  const [userData, setUserData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [editedDetails, setEditedDetails] = useState({
    fullName: "",
    gender: "",
    email: "",
    mobile: "",
    profilePhoto: null,
    previewPhoto: null,
  });

  // Add modal state
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Modal handler functions
  const showModal = (title, message, type = "info") => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeModal = () => {
    setModalConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    sessionStorage.setItem("actMod", "profile");
    setActiveModule(sessionStorage.getItem("actMod"));
  }, [setActiveModule]);

  useEffect(() => {
    fetchProfileData();
  }, []);

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      setImgError(false); // ← reset on data refresh
      setEditedDetails({
        fullName: userData.fullName || "",
        gender: userData.gender || "",
        email: userData.email || "",
        mobile: userData.mobile || "",
        profilePhoto: null,
        previewPhoto: userData.profileImage || null,
      });
    }
  }, [userData]);

  const fetchProfileData = async () => {
    try {
      const response = await axios.post(
        getProfileUrl,
        { username },
        { withCredentials: true },
      );
      console.log("Response data of User Profile:", response);
      if (response.data.user) {
        setUserData(response.data.user);
      } else {
        console.error(
          "Error fetching profile: User data not found in response",
          response.data.message,
        );
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditedDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      showModal(
        "Invalid File Type",
        "Please upload a valid image (JPEG, JPG, or PNG)",
        "error",
      );
      return;
    }

    if (file.size > 125000) {
      showModal(
        "File Too Large",
        "Image size should be less than 125KB",
        "error",
      );
      return;
    }

    setEditedDetails((prev) => ({
      ...prev,
      profilePhoto: file,
      previewPhoto: URL.createObjectURL(file),
    }));
  };

  const handleMobileChange = (e) => {
    const input = e.target.value;
    if (/^\d{0,10}$/.test(input)) {
      setEditedDetails((prev) => ({
        ...prev,
        mobile: input,
      }));
    }
  };

  const handleSave = async () => {
    try {
      const formData = new FormData();
      formData.append("username", username);
      if (editedDetails.fullName)
        formData.append("fullName", editedDetails.fullName);
      if (editedDetails.gender) formData.append("gender", editedDetails.gender);
      if (editedDetails.email) formData.append("email", editedDetails.email);
      if (editedDetails.mobile) formData.append("mobile", editedDetails.mobile);

      if (editedDetails?.profilePhoto) {
        formData.append("profilePhoto", editedDetails.profilePhoto);
      } else if (
        editedDetails?.previewPhoto === null &&
        userData?.profileImage
      ) {
        formData.append("removePhoto", "true");
      }

      const response = await axios.post(updateProfileUrl, formData, {
        withCredentials: true,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      console.log("Response data of Updated User:", response.data);
      if (response.status === 200 && response.data.success) {
        fetchProfileData();
        setUserData(response.data.user);
        setIsEditing(false);
      } else {
        showModal("Error updating profile", response.data.message, "error");
      }
    } catch (err) {
      showModal(
        "Error updating profile",
        err.response?.data?.message || err.message,
        "error",
      );
    }
  };

  const displayValue = (value) => {
    return value ? value : "N/A";
  };

  return (
    <div
      className={`${
        userProfile === "driverAssociate"
          ? "profile-page-driver"
          : "profile-page"
      }`}
    >
      {userProfile === "driverAssociate" && <DriverNavbar />}
      <div className="profile-card">
        <AlertModal
          isOpen={modalConfig.isOpen}
          onClose={closeModal}
          title={modalConfig.title}
          message={modalConfig.message}
          type={modalConfig.type}
        />
        <div className="profile-card-header">
          <button
            className="back-home-link"
            onClick={() => window.history.back()}
          >
            <span className="back-text">← Home</span>
          </button>
        </div>
        <div className="profile-avatar">
          {userData.profileImage && !imgError ? (
            <img
              src={userData.profileImage}
              alt="Profile"
              className="avatar-image"
              onError={() => setImgError(true)} // ← just set state, don't touch DOM
            />
          ) : editedDetails.previewPhoto ? (
            <img
              src={editedDetails.previewPhoto}
              alt="Profile"
              className="avatar-image"
            />
          ) : (
            <div className="avatar-icon">
              {username.charAt(0).toUpperCase()}
            </div>
          )}
          {isEditing && (
            <div className="photo-upload">
              <label htmlFor="profile-photo" className="upload-label">
                {editedDetails.previewPhoto ? "Change Photo" : "Add Photo"}
              </label>
              <input
                id="profile-photo"
                type="file"
                accept="image/jpeg, image/jpg, image/png"
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
              <div className="file-requirements">(JPEG/PNG, max 125KB)</div>
            </div>
          )}
        </div>

        <div className="profile-details">
          <div className="detail-row">
            <span className="detail-label">Full Name:</span>
            {isEditing ? (
              <div className="edit-input-container">
                <input
                  type="text"
                  name="fullName"
                  value={editedDetails.fullName}
                  onChange={handleInputChange}
                  className="edit-input"
                />
              </div>
            ) : (
              <span className="detail-value">
                {displayValue(userData.fullName)}
              </span>
            )}
          </div>

          <div className="detail-row">
            <span className="detail-label">Username:</span>
            <span className="detail-value">{username}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Role:</span>
            <span className="detail-value">{userProfile.toUpperCase()}</span>
          </div>

          <div className="detail-row">
            <span className="detail-label">Gender:</span>
            {isEditing ? (
              <div className="edit-input-container">
                <select
                  name="gender"
                  value={editedDetails.gender}
                  onChange={handleInputChange}
                  className="edit-input"
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>
            ) : (
              <span className="detail-value">
                {displayValue(userData.gender)}
              </span>
            )}
          </div>

          <div className="detail-row">
            <span className="detail-label">Email:</span>
            {isEditing ? (
              <div className="edit-input-container">
                <input
                  type="email"
                  name="email"
                  value={editedDetails.email}
                  onChange={handleInputChange}
                  className="edit-input"
                />
              </div>
            ) : (
              <span className="detail-value">
                {displayValue(userData.email)}
              </span>
            )}
          </div>

          <div className="detail-row">
            <span className="detail-label">Mobile:</span>
            {isEditing ? (
              <div className="edit-input-container">
                <input
                  type="text"
                  name="mobile"
                  value={editedDetails.mobile}
                  onChange={handleMobileChange}
                  className="edit-input"
                />
              </div>
            ) : (
              <span className="detail-value">
                {displayValue(userData.mobile)}
              </span>
            )}
            {isEditing ? (
              <div className="profile-actions">
                <button className="save-button" onClick={handleSave}>
                  Save
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                className="edit-profile-button"
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
